/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 团队状态管理 (RTK Slice)
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// 成员类型 - 与 global.d.ts 中的 User 类型保持兼容
interface GroupMember {
  user_id: string
  nickname: string
  headimgurl: string
  mobile: string
  sex?: number
  user_status?: number
  role_type?: number
  [key: string]: any
}

// VIP 信息类型
interface VipInfo {
  vip_kind?: number
  expire_time?: number
  watermarkCnt?: number
  [key: string]: any
}

// 统计信息类型
interface Statistics {
  topNeedVipKind?: number
  totalNum: number
  limitPhotoNum: number
  memberCount: number
  hdPhotoCount: number
  nonHDPhotoCount: number
  videoTotalDurationMinutes: number
  videoTotalDurationLimit: number
  [key: string]: any
}

// AI 照片标签状态
interface AiPhotoTags {
  groupId: string
  count: number
  loaded: boolean
  tags: any[]
}

// 客户配置类型
export interface CustomerConfig {
  noun?: string
  [key: string]: any
}

// 团队授权策略类型
interface GroupAuthPolicy {
  groupID?: string
  roleID?: number
  [key: string]: any
}

// State 类型
interface GroupState {
  groupName: string
  members: GroupMember[]
  groupId: string
  avatar: string
  color: string
  show_ai_photo: boolean
  show_member_location: boolean
  myRole: number
  viewPerm: number
  onlyInGroup: number
  isVip: boolean
  statistics: Statistics | null
  vipInfo: VipInfo | null
  customerConfig: CustomerConfig
  aiPhotoTags: AiPhotoTags
  groupAuthPolicy: GroupAuthPolicy | null
  photoViewLimit: boolean
  taskForm: { enabled: boolean }
  // SET_GROUP_INFO 中设置的额外字段
  invite_id?: string
  group_lucky_id?: string
  industryCode?: string
  photo_agg_type?: number
  itemCnt?: number
  groupSize?: number
  groupSizeLimit?: number
  isOpenNeteaseShelfReview?: boolean
}

const initialState: GroupState = {
  groupName: '',
  members: [],
  groupId: '',
  avatar: '',
  color: '',
  show_ai_photo: false,
  show_member_location: false,
  myRole: -1,
  viewPerm: 1,
  onlyInGroup: 0,
  isVip: false,
  statistics: null,
  vipInfo: null,
  customerConfig: {},
  aiPhotoTags: {
    groupId: '',
    count: 0,
    loaded: false,
    tags: []
  },
  groupAuthPolicy: null,
  photoViewLimit: false,
  taskForm: { enabled: false }
}

const groupSlice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    // 设置团队信息（对应 SET_GROUP_INFO）
    setGroupInfo(
      state,
      action: PayloadAction<{
        group_name: string
        user_list: GroupMember[]
        group_view_perm: number
        invite_id?: string
        group_lucky_id?: string
        is_vip: boolean
        vip_info?: VipInfo | null
        industryCode?: string
        avatar?: string
        group_color?: string
        show_ai_photo?: boolean
        show_member_location?: boolean
        photo_agg_type?: number
        itemCnt?: number
        groupSize?: number
        groupSizeLimit?: number
        isOpenNeteaseShelfReview?: boolean
        statistics?: Statistics | null
        taskForm?: { enabled: boolean } | null
      }>
    ) {
      const data = action.payload
      state.groupName = data.group_name
      state.members = data.user_list
      state.viewPerm = data.group_view_perm
      state.invite_id = data.invite_id
      state.group_lucky_id = data.group_lucky_id
      state.isVip = data.is_vip
      state.vipInfo = data.vip_info || null
      state.industryCode = data.industryCode
      state.avatar = data.avatar || ''
      state.color = data.group_color || ''
      state.show_ai_photo = data.show_ai_photo || false
      state.show_member_location = data.show_member_location || false
      state.photo_agg_type = data.photo_agg_type
      state.itemCnt = data.itemCnt
      state.groupSize = data.groupSize
      state.groupSizeLimit = data.groupSizeLimit
      state.isOpenNeteaseShelfReview = data.isOpenNeteaseShelfReview
      state.statistics = data.statistics || null
      state.taskForm = data.taskForm || { enabled: false }
    },

    // 设置团队成员（对应 SET_GROUP_MEMBERS）
    setGroupMembers(state, action: PayloadAction<GroupMember[]>) {
      state.members = action.payload
    },

    // 设置成员昵称（对应 SET_GROUP_MEMBER_NICKNAME）
    setGroupMemberNickname(
      state,
      action: PayloadAction<{ userId: string | number; newName: string }>
    ) {
      const { userId, newName } = action.payload
      const memberIndex = state.members.findIndex(
        (member) => member.user_id === userId
      )
      if (memberIndex !== -1) {
        state.members[memberIndex].nickname = newName
      }
    },

    // 设置团队ID（对应 SET_GROUP_ID）
    setGroupId(state, action: PayloadAction<string>) {
      state.groupId = action.payload
    },

    // 设置仅团队内（对应 SET_ONLY_IN_GROUP）
    setOnlyInGroup(state, action: PayloadAction<number>) {
      state.onlyInGroup = action.payload
    },

    // 设置查看权限（对应 SET_GROUP_VIEW_PERM）
    setGroupViewPerm(state, action: PayloadAction<number>) {
      state.viewPerm = action.payload
    },

    // 设置我的角色（对应 SET_MY_ROLE）
    setMyRole(state, action: PayloadAction<number>) {
      state.myRole = action.payload
    },

    // 清除团队信息（对应 CLEAR_GROUP）
    clearGroup() {
      return initialState
    },

    // 设置客户配置（对应 SET_GROUP_CUSTOMER_CONFIG）
    setGroupCustomerConfig(state, action: PayloadAction<CustomerConfig>) {
      state.customerConfig = action.payload
    },

    // 设置AI照片标签状态（对应 SET_AI_PHOTO_TAGS_STATE）
    setAiPhotoTagsState(state, action: PayloadAction<AiPhotoTags>) {
      state.aiPhotoTags = action.payload
    },

    // 设置照片统计（对应 SET_PHOTO_STATISTICS）
    setPhotoStatistics(state, action: PayloadAction<Statistics | null>) {
      state.statistics = action.payload
    },

    // 设置团队授权策略（对应 SET_GROUP_AUTH_POLICY）
    setGroupAuthPolicy(state, action: PayloadAction<GroupAuthPolicy | null>) {
      state.groupAuthPolicy = action.payload
    }
  }
})

export const {
  setGroupInfo,
  setGroupMembers,
  setGroupMemberNickname,
  setGroupId,
  setOnlyInGroup,
  setGroupViewPerm,
  setMyRole,
  clearGroup,
  setGroupCustomerConfig,
  setAiPhotoTagsState,
  setPhotoStatistics,
  setGroupAuthPolicy
} = groupSlice.actions

export default groupSlice.reducer

// 类型导出，供组件使用
export type {
  GroupState,
  GroupMember,
  VipInfo,
  Statistics,
  AiPhotoTags,
  GroupAuthPolicy
}
